import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tree", "loading", "createForm", "createInput", "createError", "parentPath"]
  static values = {
    url: String,
    foldersUrl: String,
    currentFolder: String
  }

  connect() {
    this.selectedFolder = this.currentFolderValue || null
    this.loadFolders()
  }

  showCreateInput(event) {
    event?.preventDefault()

    // Show the create form
    this.createFormTarget.classList.remove("hidden")

    // Update parent path display
    if (this.selectedFolder) {
      this.parentPathTarget.textContent = ` in ${this.selectedFolder}/`
    } else {
      this.parentPathTarget.textContent = ""
    }

    // Clear input and focus
    this.createInputTarget.value = ""
    this.hideError()
    this.createInputTarget.focus()
  }

  cancelCreate(event) {
    event?.preventDefault()
    this.createFormTarget.classList.add("hidden")
    this.createInputTarget.value = ""
    this.hideError()
  }

  async createFolder(event) {
    event.preventDefault()

    const folderName = this.createInputTarget.value.trim()
    if (!folderName) return

    // Build full path based on current context
    const fullPath = this.selectedFolder
      ? `${this.selectedFolder}/${folderName}`
      : folderName

    this.hideError()

    try {
      const response = await fetch(this.foldersUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ path: fullPath })
      })

      const data = await response.json()

      if (response.ok) {
        this.cancelCreate()
        this.loadFolders()
        this.showSuccess(data.message || "Folder created successfully")
      } else {
        this.showError(data.error || "Failed to create folder")
      }
    } catch (error) {
      console.error("Error creating folder:", error)
      this.showError("Failed to create folder")
    }
  }

  async loadFolders() {
    try {
      this.showLoading()

      const response = await fetch(this.urlValue)
      if (!response.ok) throw new Error("Failed to load folders")

      const data = await response.json()
      this.renderTree(data.folders)
    } catch (error) {
      console.error("Error loading folders:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  renderTree(folders) {
    if (!folders || folders.length === 0) {
      this.treeTarget.innerHTML = `
        <div class="p-4 text-sm text-gray-500 text-center">
          No folders yet
        </div>
      `
      return
    }

    // Build hierarchical structure
    const rootFolders = this.buildHierarchy(folders)
    this.treeTarget.innerHTML = this.renderFolders(rootFolders)
  }

  buildHierarchy(folders) {
    const folderMap = {}
    const rootFolders = []

    // Create map of all folders
    folders.forEach(folder => {
      folderMap[folder.path] = { ...folder, children: [] }
    })

    // Build parent-child relationships
    folders.forEach(folder => {
      const parts = folder.path.split("/")
      if (parts.length === 1) {
        // Root level folder
        rootFolders.push(folderMap[folder.path])
      } else {
        // Child folder - find parent
        const parentPath = parts.slice(0, -1).join("/")
        if (folderMap[parentPath]) {
          folderMap[parentPath].children.push(folderMap[folder.path])
        }
      }
    })

    return rootFolders
  }

  renderFolders(folders, level = 0) {
    return folders.map(folder => {
      const isActive = this.currentFolderValue === folder.path
      const hasChildren = folder.children && folder.children.length > 0
      const indent = level * 16

      return `
        <div class="folder-item" data-folder-path="${folder.path}">
          <div class="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer rounded-lg ${isActive ? 'bg-green-50 text-green-700' : 'text-gray-700'}"
               style="padding-left: ${indent + 12}px"
               data-action="click->folder-tree#selectFolder contextmenu->folder-tree#showContextMenu">
            ${hasChildren ? `
              <button type="button"
                      class="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
                      data-action="click->folder-tree#toggleFolder:stop">
                <i class="fas fa-chevron-right text-xs transition-transform" data-folder-tree-target="chevron"></i>
              </button>
            ` : '<span class="w-4"></span>'}
            <i class="fas fa-folder text-yellow-500 flex-shrink-0"></i>
            <span class="flex-1 truncate font-medium">${folder.name}</span>
            <span class="flex-shrink-0 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">${folder.count}</span>
          </div>
          ${hasChildren ? `
            <div class="folder-children hidden">
              ${this.renderFolders(folder.children, level + 1)}
            </div>
          ` : ''}
        </div>
      `
    }).join("")
  }

  selectFolder(event) {
    event.preventDefault()
    const folderItem = event.currentTarget.closest(".folder-item")
    const folderPath = folderItem.dataset.folderPath

    // Track selected folder for context
    this.selectedFolder = folderPath

    // Navigate to filtered view
    const url = new URL(window.location.href)
    url.searchParams.set("folder", folderPath)
    window.location.href = url.toString()
  }

  toggleFolder(event) {
    event.preventDefault()
    const folderItem = event.currentTarget.closest(".folder-item")
    const childrenContainer = folderItem.querySelector(".folder-children")
    const chevron = event.currentTarget.querySelector("i")

    if (childrenContainer) {
      childrenContainer.classList.toggle("hidden")
      chevron.classList.toggle("rotate-90")
    }
  }

  showContextMenu(event) {
    event.preventDefault()
    const folderItem = event.currentTarget.closest(".folder-item")
    const folderPath = folderItem.dataset.folderPath

    // Dispatch event for modal controller to show context menu
    this.dispatch("contextMenu", {
      detail: {
        path: folderPath,
        x: event.clientX,
        y: event.clientY
      }
    })
  }

  // Called from folder modal controller after successful operations
  refresh() {
    this.loadFolders()
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (this.hasCreateErrorTarget) {
      this.createErrorTarget.classList.remove("hidden")
      this.createErrorTarget.textContent = message
    } else {
      // Tree load error
      this.treeTarget.innerHTML = `
        <div class="p-4 text-sm text-red-600 text-center">
          <i class="fas fa-exclamation-triangle mb-2"></i>
          <p>Failed to load folders</p>
          <button type="button"
                  class="mt-2 text-xs text-red-700 underline hover:no-underline"
                  data-action="click->folder-tree#loadFolders">
            Retry
          </button>
        </div>
      `
    }
  }

  hideError() {
    if (this.hasCreateErrorTarget) {
      this.createErrorTarget.classList.add("hidden")
      this.createErrorTarget.textContent = ""
    }
  }

  showSuccess(message) {
    // Simple alert for now - could be replaced with a toast notification
    alert(message)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
